import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../hooks/use-auth';
import { SidebarLayout } from '../components/layout';
import { Card } from '../components/layout';
import { Button } from '../components/ui/button';
import { Input, Textarea, Select } from '../components/ui/form';
import { Modal } from '../components/ui/modal';
import { useDivisionContext } from '../contexts/division-context';
import { useDivisionRoles } from '../hooks/use-division-roles';
import { useDivisionOrderAttributeTemplates } from '../hooks/use-division-order-attribute-templates';
import { getStepTypeLabel } from '../utils';
import {
  useWorkflowTemplates,
  useCreateWorkflowTemplate,
  useUpdateWorkflowTemplate,
  useDeleteWorkflowTemplate,
} from '../hooks/use-workflow-templates';
import {
  WorkflowTemplateDto,
  RoutingRuleTemplateDto,
  StepTemplateDto,
  OrderAttributeTemplateDto,
  CreateWorkflowTemplateRequest,
  UpdateWorkflowTemplateRequest,
} from '../types';

const ROUTING_FIELDS = [
  { value: 'totalAmount', label: 'Total Amount' },
  { value: 'submitterId', label: 'Submitter' },
  { value: 'vendorId', label: 'Vendor' },
  { value: 'departmentCode', label: 'Department Code' },
  { value: 'jsonPath', label: 'Custom JSONPath' },
];

const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'greater-than', label: 'Greater Than' },
  { value: 'less-than', label: 'Less Than' },
  { value: 'between', label: 'Between' },
];

const FIELD_TYPES = ['string', 'number', 'boolean', 'date'];
const WORKFLOW_TYPES = ['approval', 'purchase_order', 'expense_report', 'invoice', 'other'];

// ─── Blank template factory ───────────────────────────────────────────────────

function blankTemplate(): CreateWorkflowTemplateRequest {
  return {
    name: '',
    description: '',
    type: 'purchase_order',
    routingRulesTemplate: [],
    stepsTemplate: [],
    orderAttributesTemplate: [],
  };
}

// ─── Template editor modal ────────────────────────────────────────────────────

interface TemplateEditorProps {
  divisionId: string;
  template?: WorkflowTemplateDto; // undefined → create mode
  onClose: () => void;
}

function TemplateEditor({ divisionId, template, onClose }: TemplateEditorProps) {
  const isEdit = !!template;

  const [form, setForm] = useState<CreateWorkflowTemplateRequest>(
    template
      ? {
          name: template.name,
          description: template.description ?? '',
          type: template.type,
          routingRulesTemplate: template.routingRulesTemplate,
          stepsTemplate: template.stepsTemplate,
          orderAttributesTemplate: template.orderAttributesTemplate,
        }
      : blankTemplate()
  );
  const [activeSection, setActiveSection] = useState<'routing' | 'steps' | 'attributes'>('routing');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreateWorkflowTemplate(divisionId);
  const updateMutation = useUpdateWorkflowTemplate(divisionId, template?.id ?? '');
  const { data: divisionRolesResponse } = useDivisionRoles(divisionId);
  const divisionRoles = divisionRolesResponse?.roles ?? [];
  const { data: divisionAttributeTemplates } = useDivisionOrderAttributeTemplates(divisionId);
  const divisionAttributeJsonPaths = new Set(
    (divisionAttributeTemplates ?? []).map(d => d.jsonPath.trim().toLowerCase())
  );

  const saving = createMutation.isPending || updateMutation.isPending;

  // Seed the attributes list with any division-required attributes not already present (matched
  // by JsonPath), so admins see them here and can configure MinStep/MaxStep/Required/Editable
  // overrides instead of the tab appearing empty even though the division has required attributes.
  const seededDivisionAttributesRef = useRef(false);
  useEffect(() => {
    if (seededDivisionAttributesRef.current) return;
    if (!divisionAttributeTemplates || divisionAttributeTemplates.length === 0) return;
    seededDivisionAttributesRef.current = true;

    const existingPaths = new Set(form.orderAttributesTemplate.map(a => a.jsonPath.trim().toLowerCase()));
    const missing = divisionAttributeTemplates
      .filter(d => !existingPaths.has(d.jsonPath.trim().toLowerCase()))
      .map((d, i): OrderAttributeTemplateDto => ({
        jsonPath: d.jsonPath,
        label: d.label,
        fieldType: d.fieldType,
        description: d.description,
        isRequired: true,
        isEditable: true,
        showValueInList: d.showValueInList,
        allowedValuesJsonPath: d.allowedValuesJsonPath,
        displayOrder: form.orderAttributesTemplate.length + i + 1,
      }));

    if (missing.length > 0) {
      setField('orderAttributesTemplate', [...form.orderAttributesTemplate, ...missing]);
    }
  }, [divisionAttributeTemplates]);

  const setField = <K extends keyof CreateWorkflowTemplateRequest>(
    key: K,
    value: CreateWorkflowTemplateRequest[K]
  ) => setForm(prev => ({ ...prev, [key]: value }));

  // ── Routing rules ──────────────────────────────────────────────────────────

  const addRoutingRule = () => {
    const rule: RoutingRuleTemplateDto = {
      id: crypto.randomUUID(),
      field: 'totalAmount',
      operator: 'greater-than',
      customPath: undefined,
    };
    setField('routingRulesTemplate', [...form.routingRulesTemplate, rule]);
  };

  const updateRoutingRule = (id: string, patch: Partial<RoutingRuleTemplateDto>) => {
    setField(
      'routingRulesTemplate',
      form.routingRulesTemplate.map(r => (r.id === id ? { ...r, ...patch } : r))
    );
  };

  const removeRoutingRule = (id: string) => {
    setField('routingRulesTemplate', form.routingRulesTemplate.filter(r => r.id !== id));
  };

  // ── Steps ──────────────────────────────────────────────────────────────────

  const addStep = () => {
    const step: StepTemplateDto = {
      step: (form.stepsTemplate.length > 0
        ? Math.max(...form.stepsTemplate.map(s => s.step))
        : 0) + 1,
      name: `Step ${form.stepsTemplate.length + 1}`,
      stepType: 'approval',
      canEditQuantity: false,
      allowQuantityIncrease: false,
      parallelApproval: false,
      requiredApprovals: 1,
      orderLevelOnly: false,
      skipIfApprovedBy: [],
      bypassIfNoApprover: false,
      auditBypass: false,
      requireExplicitSubmit: false,
    };
    setField('stepsTemplate', [...form.stepsTemplate, step]);
  };

  const updateStep = (index: number, patch: Partial<StepTemplateDto>) => {
    const updated = form.stepsTemplate.map((s, i) => (i === index ? { ...s, ...patch } : s));
    setField('stepsTemplate', updated);
  };

  const removeStep = (index: number) => {
    setField('stepsTemplate', form.stepsTemplate.filter((_, i) => i !== index));
  };

  const addSkipRole = (index: number, role: string) => {
    const trimmed = role.trim();
    if (!trimmed) return;
    const step = form.stepsTemplate[index];
    if ((step.skipIfApprovedBy ?? []).includes(trimmed)) return;
    updateStep(index, { skipIfApprovedBy: [...(step.skipIfApprovedBy ?? []), trimmed] });
  };

  const removeSkipRole = (index: number, role: string) => {
    const step = form.stepsTemplate[index];
    updateStep(index, { skipIfApprovedBy: (step.skipIfApprovedBy ?? []).filter(r => r !== role) });
  };

  // ── Order attributes ───────────────────────────────────────────────────────

  const addAttribute = () => {
    const attr: OrderAttributeTemplateDto = {
      jsonPath: '',
      label: '',
      fieldType: 'string',
      isRequired: false,
      isEditable: true,
      showValueInList: false,
      displayOrder: form.orderAttributesTemplate.length + 1,
    };
    setField('orderAttributesTemplate', [...form.orderAttributesTemplate, attr]);
  };

  const updateAttribute = (index: number, patch: Partial<OrderAttributeTemplateDto>) => {
    setField(
      'orderAttributesTemplate',
      form.orderAttributesTemplate.map((a, i) => (i === index ? { ...a, ...patch } : a))
    );
  };

  const removeAttribute = (index: number) => {
    setField('orderAttributesTemplate', form.orderAttributesTemplate.filter((_, i) => i !== index));
  };

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Template name is required.');
      return;
    }
    setError(null);
    try {
      if (isEdit) {
        await updateMutation.mutateAsync({
          ...form,
          isActive: true,
        } as UpdateWorkflowTemplateRequest);
      } else {
        await createMutation.mutateAsync(form);
      }
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save template.');
    }
  };

  // ── Section tabs ───────────────────────────────────────────────────────────

  const tabs: { id: typeof activeSection; label: string; count: number }[] = [
    { id: 'routing', label: 'Routing Rules', count: form.routingRulesTemplate.length },
    { id: 'steps', label: 'Workflow Steps', count: form.stepsTemplate.length },
    { id: 'attributes', label: 'Order Attributes', count: form.orderAttributesTemplate.length },
  ];

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? `Edit Template: ${template!.name}` : 'New Workflow Template'}
      size="xl"
    >
      <div className="space-y-6">
        {/* Basics */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Template Name *"
            value={form.name}
            onChange={e => setField('name', e.target.value)}
            placeholder="e.g. Standard PO Approval"
          />
          <Select
            label="Workflow Type"
            value={form.type}
            onChange={e => setField('type', e.target.value)}
          >
            {WORKFLOW_TYPES.map(t => (
              <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
            ))}
          </Select>
        </div>
        <Textarea
          label="Description"
          value={form.description ?? ''}
          onChange={e => setField('description', e.target.value)}
          rows={2}
          placeholder="Optional description of what this template is used for"
        />

        {/* Section tabs */}
        <div className="border-b border-gray-200 dark:border-secondary-700">
          <nav className="flex space-x-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeSection === tab.id
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200'
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className="ml-2 bg-gray-100 dark:bg-secondary-700 text-gray-600 dark:text-secondary-400 text-xs px-2 py-0.5 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* ── Routing Rules section ──────────────────────────────────────── */}
        {activeSection === 'routing' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-500 dark:text-secondary-400">
              Define which fields and operators to match on. Values are left blank — they will be
              filled in when creating a workflow from this template.
            </p>
            {form.routingRulesTemplate.map(rule => (
              <div key={rule.id} className="flex flex-col gap-2 p-3 bg-gray-50 dark:bg-secondary-900 rounded-lg">
                <div className="flex items-end gap-3">
                  <Select
                    label="Field"
                    value={rule.field}
                    onChange={e => updateRoutingRule(rule.id, { field: e.target.value, customPath: undefined })}
                  >
                    {ROUTING_FIELDS.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </Select>
                  <Select
                    label="Operator"
                    value={rule.operator}
                    onChange={e => updateRoutingRule(rule.id, { operator: e.target.value })}
                  >
                    {OPERATORS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                  <Input
                    label="Label (optional)"
                    value={rule.label ?? ''}
                    onChange={e => updateRoutingRule(rule.id, { label: e.target.value })}
                    placeholder="hint for user"
                  />
                  <button
                    onClick={() => removeRoutingRule(rule.id)}
                    className="mb-1 p-2 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {rule.field === 'jsonPath' && (
                  <Input
                    label="JSONPath Expression"
                    value={rule.customPath ?? ''}
                    onChange={e => updateRoutingRule(rule.id, { customPath: e.target.value })}
                    placeholder="e.g. $.vendor.category"
                    className="font-mono text-sm"
                  />
                )}
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addRoutingRule}>
              <Plus className="h-4 w-4 mr-1" /> Add Routing Rule
            </Button>
          </div>
        )}

        {/* ── Steps section ──────────────────────────────────────────────── */}
        {activeSection === 'steps' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-500 dark:text-secondary-400">
              Define the approval steps. Approvers are not assigned here — they are assigned when
              creating a workflow from this template, or automatically at public registration based
              on the Registration Role Name below.
            </p>
            {form.stepsTemplate.map((step, idx) => (
              <div key={idx} className="p-4 bg-gray-50 dark:bg-secondary-900 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700 dark:text-secondary-200">Step {step.step}</span>
                  <button
                    onClick={() => removeStep(idx)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Step Name"
                    value={step.name}
                    onChange={e => updateStep(idx, { name: e.target.value })}
                  />
                  <Select
                    label="Step Type"
                    value={step.stepType}
                    onChange={e => updateStep(idx, { stepType: e.target.value })}
                  >
                    <option value="approval">Approval</option>
                    <option value="fulfillment">Fulfillment</option>
                    <option value="notification">Notification</option>
                  </Select>
                </div>
                <Input
                  label="Description"
                  value={step.description ?? ''}
                  onChange={e => updateStep(idx, { description: e.target.value })}
                />
                <Select
                  label="Registration Role Name (optional)"
                  value={step.registrationRoleName ?? ''}
                  onChange={e => updateStep(idx, { registrationRoleName: e.target.value || undefined })}
                  helperText="When this template is used by public registration, the user who registers with this division role name is assigned to this step."
                >
                  <option value="">None</option>
                  {divisionRoles.map(role => (
                    <option key={role.id} value={role.name}>
                      {role.name}
                    </option>
                  ))}
                  {step.registrationRoleName &&
                    !divisionRoles.some(role => role.name === step.registrationRoleName) && (
                      <option value={step.registrationRoleName}>{step.registrationRoleName} (not in this division)</option>
                    )}
                </Select>
                <div className="flex flex-wrap gap-6 items-center">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-secondary-200 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={step.canEditQuantity}
                      onChange={e => updateStep(idx, { canEditQuantity: e.target.checked })}
                      className="rounded border-gray-300 dark:border-secondary-600"
                    />
                    Approver can edit quantities at this step
                  </label>
                  <label className={`flex items-center gap-2 text-sm text-gray-700 dark:text-secondary-200 ${step.canEditQuantity ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                    <input
                      type="checkbox"
                      checked={step.allowQuantityIncrease ?? false}
                      onChange={e => updateStep(idx, { allowQuantityIncrease: e.target.checked })}
                      disabled={!step.canEditQuantity}
                      className="rounded border-gray-300 dark:border-secondary-600 disabled:cursor-not-allowed"
                    />
                    Allow increasing quantities (uncheck to only allow decreasing)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-secondary-200 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={step.orderLevelOnly ?? false}
                      onChange={e => updateStep(idx, { orderLevelOnly: e.target.checked })}
                      className="rounded border-gray-300 dark:border-secondary-600"
                    />
                    Order-level approval only
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-secondary-200 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={step.requireExplicitSubmit ?? false}
                      onChange={e => updateStep(idx, { requireExplicitSubmit: e.target.checked })}
                      className="rounded border-gray-300 dark:border-secondary-600"
                    />
                    Require explicit "Submit"
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-secondary-200 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={step.parallelApproval}
                      onChange={e => updateStep(idx, { parallelApproval: e.target.checked })}
                      className="rounded border-gray-300 dark:border-secondary-600"
                    />
                    Parallel approval
                  </label>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700 dark:text-secondary-200">Required approvals</label>
                    <input
                      type="number"
                      min={1}
                      value={step.requiredApprovals}
                      onChange={e => updateStep(idx, { requiredApprovals: parseInt(e.target.value) || 1 })}
                      className="w-16 border border-gray-300 dark:border-secondary-600 rounded px-2 py-1 text-sm bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700 dark:text-secondary-200">Timeout (hrs)</label>
                    <input
                      type="number"
                      min={1}
                      value={step.timeoutHours ?? ''}
                      onChange={e =>
                        updateStep(idx, {
                          timeoutHours: e.target.value ? parseInt(e.target.value) : undefined,
                        })
                      }
                      placeholder="none"
                      className="w-20 border border-gray-300 dark:border-secondary-600 rounded px-2 py-1 text-sm bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700 dark:text-secondary-200">Timeout warning (hrs before)</label>
                    <input
                      type="number"
                      min={1}
                      value={step.timeoutWarningHours ?? ''}
                      onChange={e =>
                        updateStep(idx, {
                          timeoutWarningHours: e.target.value ? parseInt(e.target.value) : undefined,
                        })
                      }
                      placeholder="none"
                      className="w-20 border border-gray-300 dark:border-secondary-600 rounded px-2 py-1 text-sm bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    />
                  </div>
                </div>

                {/* Conditions */}
                <div className="border-t border-gray-200 dark:border-secondary-700 pt-3 space-y-3">
                  <h5 className="text-xs font-semibold text-gray-500 dark:text-secondary-400 uppercase">Conditions</h5>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700 dark:text-secondary-200">Amount threshold</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={step.amountThreshold ?? ''}
                      onChange={e =>
                        updateStep(idx, {
                          amountThreshold: e.target.value ? parseFloat(e.target.value) : undefined,
                        })
                      }
                      placeholder="none"
                      className="w-28 border border-gray-300 dark:border-secondary-600 rounded px-2 py-1 text-sm bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-700 dark:text-secondary-200">Skip if already approved by role</label>
                    <div className="flex flex-wrap gap-1.5">
                      {(step.skipIfApprovedBy ?? []).map(role => (
                        <span
                          key={role}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-200 dark:bg-secondary-700 text-gray-700 dark:text-secondary-200 text-xs"
                        >
                          {role}
                          <button
                            type="button"
                            onClick={() => removeSkipRole(idx, role)}
                            className="ml-1 text-red-500 hover:text-red-700"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Role name (e.g., Admin, DivisionAdmin)"
                        className="flex-1 border border-gray-300 dark:border-secondary-600 rounded px-2 py-1 text-sm bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                        onKeyPress={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const input = e.target as HTMLInputElement;
                            addSkipRole(idx, input.value);
                            input.value = '';
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={e => {
                          const input = (e.target as HTMLElement).parentElement?.querySelector('input') as HTMLInputElement;
                          if (input) {
                            addSkipRole(idx, input.value);
                            input.value = '';
                          }
                        }}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-secondary-200 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={step.bypassIfNoApprover ?? false}
                        onChange={e => updateStep(idx, { bypassIfNoApprover: e.target.checked })}
                        className="rounded border-gray-300 dark:border-secondary-600"
                      />
                      Bypass step if no approver is assigned
                    </label>
                    {step.bypassIfNoApprover && (
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-secondary-200 cursor-pointer ml-6">
                        <input
                          type="checkbox"
                          checked={step.auditBypass ?? false}
                          onChange={e => updateStep(idx, { auditBypass: e.target.checked })}
                          className="rounded border-gray-300 dark:border-secondary-600"
                        />
                        Record bypass in audit log
                      </label>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addStep}>
              <Plus className="h-4 w-4 mr-1" /> Add Step
            </Button>
          </div>
        )}

        {/* ── Order Attributes section ───────────────────────────────────── */}
        {activeSection === 'attributes' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-500 dark:text-secondary-400">
              Define order attributes — their visibility, whether they're required, and which steps
              can edit them. If a JSON Path here matches a division-level required attribute, these
              settings override that attribute's default (required, editable at every step) for
              workflows created from this template.
            </p>
            {form.orderAttributesTemplate.map((attr, idx) => (
              <div key={idx} className="p-4 bg-gray-50 dark:bg-secondary-900 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700 dark:text-secondary-200 flex items-center gap-2">
                    {attr.label || `Attribute ${idx + 1}`}
                    {divisionAttributeJsonPaths.has(attr.jsonPath.trim().toLowerCase()) && (
                      <span className="text-xs font-normal px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                        Division Required
                      </span>
                    )}
                  </span>
                  <button
                    onClick={() => removeAttribute(idx)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    label="Label"
                    value={attr.label}
                    onChange={e => updateAttribute(idx, { label: e.target.value })}
                  />
                  <Input
                    label="JSON Path"
                    value={attr.jsonPath}
                    onChange={e => updateAttribute(idx, { jsonPath: e.target.value })}
                    placeholder="$.notes"
                  />
                  <Select
                    label="Field Type"
                    value={attr.fieldType}
                    onChange={e => updateAttribute(idx, { fieldType: e.target.value })}
                  >
                    {FIELD_TYPES.map(t => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </Select>
                </div>
                <Input
                  label="Description"
                  value={attr.description ?? ''}
                  onChange={e => updateAttribute(idx, { description: e.target.value })}
                />
                <Input
                  label="Allowed Values JSON Path"
                  value={attr.allowedValuesJsonPath ?? ''}
                  onChange={e => updateAttribute(idx, { allowedValuesJsonPath: e.target.value || undefined })}
                  placeholder="$.Order_Attributes[?(@.Name == 'FieldName')].AllowedValues"
                  className="font-mono text-sm"
                  helperText="Optional JSONPath to an array of {Name, Value} objects in the order document. When set, the field renders as a select list."
                />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wide">Flags</p>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-secondary-200 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={attr.isRequired}
                        onChange={e => updateAttribute(idx, { isRequired: e.target.checked })}
                        className="rounded border-gray-300 dark:border-secondary-600"
                      />
                      Required
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-secondary-200 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={attr.isEditable}
                        onChange={e => updateAttribute(idx, { isEditable: e.target.checked })}
                        className="rounded border-gray-300 dark:border-secondary-600"
                      />
                      Editable
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-secondary-200 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={attr.showValueInList}
                        onChange={e => updateAttribute(idx, { showValueInList: e.target.checked })}
                        className="rounded border-gray-300 dark:border-secondary-600"
                      />
                      Show in order list
                    </label>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wide">
                      Editable at steps
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <label className="text-sm text-gray-600 dark:text-secondary-400">From step</label>
                        <input
                          type="number"
                          min={1}
                          value={attr.minStep ?? ''}
                          onChange={e =>
                            updateAttribute(idx, {
                              minStep: e.target.value ? parseInt(e.target.value) : undefined,
                            })
                          }
                          placeholder="any"
                          className="w-16 border border-gray-300 dark:border-secondary-600 rounded px-2 py-1 text-sm bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <label className="text-sm text-gray-600 dark:text-secondary-400">to step</label>
                        <input
                          type="number"
                          min={1}
                          value={attr.maxStep ?? ''}
                          onChange={e =>
                            updateAttribute(idx, {
                              maxStep: e.target.value ? parseInt(e.target.value) : undefined,
                            })
                          }
                          placeholder="any"
                          className="w-16 border border-gray-300 dark:border-secondary-600 rounded px-2 py-1 text-sm bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addAttribute}>
              <Plus className="h-4 w-4 mr-1" /> Add Attribute
            </Button>
          </div>
        )}

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="flex justify-end gap-3 pt-2 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Template'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Template card ────────────────────────────────────────────────────────────

interface TemplateCardProps {
  template: WorkflowTemplateDto;
  onEdit: () => void;
  onDelete: () => void;
  readOnly?: boolean;
  /** Deleting a template is restricted to Admin/DivisionAdmin - WorkflowAdmin can edit but not delete. */
  canDelete?: boolean;
}

function TemplateCard({ template, onEdit, onDelete, readOnly, canDelete = true }: TemplateCardProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="p-0 overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">{template.name}</h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950 text-blue-700 border border-blue-200 dark:border-blue-800 shrink-0">
                {template.type.replace(/_/g, ' ')}
              </span>
              {!template.isActive && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-secondary-700 text-gray-500 dark:text-secondary-400 shrink-0">
                  Inactive
                </span>
              )}
            </div>
            {template.description && (
              <p className="text-sm text-gray-500 dark:text-secondary-400 mt-1 truncate">{template.description}</p>
            )}
            <div className="flex gap-4 mt-2 text-xs text-gray-500 dark:text-secondary-400">
              <span>{template.stepsTemplate.length} step{template.stepsTemplate.length !== 1 ? 's' : ''}</span>
              <span>{template.routingRulesTemplate.length} routing rule{template.routingRulesTemplate.length !== 1 ? 's' : ''}</span>
              <span>{template.orderAttributesTemplate.length} attribute{template.orderAttributesTemplate.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0 ml-4">
            <button
              onClick={() => setExpanded(e => !e)}
              className="p-2 text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 hover:bg-gray-50 dark:hover:bg-secondary-700 rounded"
              title={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {!readOnly && (
              <button
                onClick={onEdit}
                className="p-2 text-gray-400 dark:text-secondary-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                title="Edit"
              >
                <Edit2 className="h-4 w-4" />
              </button>
            )}
            {!readOnly && canDelete && (
              <button
                onClick={onDelete}
                className="p-2 text-gray-400 dark:text-secondary-500 hover:text-red-600 hover:bg-red-50 rounded"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 dark:border-secondary-700 bg-gray-50 dark:bg-secondary-900 px-4 pb-4 pt-3 space-y-4">
          {/* Steps */}
          {template.stepsTemplate.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-2">Steps</p>
              <div className="space-y-1">
                {template.stepsTemplate.map(step => (
                  <div key={step.step} className="flex items-center gap-3 text-sm text-gray-700 dark:text-secondary-200">
                    <span className="text-gray-400 dark:text-secondary-500 w-5 text-right">{step.step}.</span>
                    <span className="font-medium">{step.name}</span>
                    <span className="text-gray-400 dark:text-secondary-500 text-xs">{getStepTypeLabel(step.stepType)}</span>
                    {step.canEditQuantity && (
                      <span className="text-xs bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 px-1.5 py-0.5 rounded">
                        qty editable
                      </span>
                    )}
                    {step.canEditQuantity && step.allowQuantityIncrease === false && (
                      <span className="text-xs bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 px-1.5 py-0.5 rounded">
                        decrease only
                      </span>
                    )}
                    {step.parallelApproval && (
                      <span className="text-xs bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-800 px-1.5 py-0.5 rounded">
                        parallel
                      </span>
                    )}
                    {step.requireExplicitSubmit && (
                      <span className="text-xs bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800 px-1.5 py-0.5 rounded">
                        require submit
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Routing rules */}
          {template.routingRulesTemplate.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-2">Routing Rules</p>
              <div className="space-y-1">
                {template.routingRulesTemplate.map(rule => (
                  <div key={rule.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-secondary-200">
                    <span className="font-medium">{rule.label || rule.field}</span>
                    <span className="text-gray-400 dark:text-secondary-500">{rule.operator}</span>
                    <span className="text-gray-400 dark:text-secondary-500 italic text-xs">&lt;value to be set&gt;</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Order attributes */}
          {template.orderAttributesTemplate.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-2">Order Attributes</p>
              <div className="overflow-x-auto">
                <table className="text-xs text-gray-700 dark:text-secondary-200 w-full">
                  <thead>
                    <tr className="text-gray-400 dark:text-secondary-500 text-left">
                      <th className="pr-4 pb-1 font-medium">Label</th>
                      <th className="pr-4 pb-1 font-medium">Type</th>
                      <th className="pr-4 pb-1 font-medium">Required</th>
                      <th className="pr-4 pb-1 font-medium">Editable</th>
                      <th className="pb-1 font-medium">Steps</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {template.orderAttributesTemplate.map((attr, i) => (
                      <tr key={i}>
                        <td className="pr-4 py-1">{attr.label}</td>
                        <td className="pr-4 py-1 text-gray-400 dark:text-secondary-500">{attr.fieldType}</td>
                        <td className="pr-4 py-1">{attr.isRequired ? 'Yes' : 'No'}</td>
                        <td className="pr-4 py-1">{attr.isEditable ? 'Yes' : 'No'}</td>
                        <td className="py-1 text-gray-400 dark:text-secondary-500">
                          {attr.minStep || attr.maxStep
                            ? `${attr.minStep ?? '1'} – ${attr.maxStep ?? '∞'}`
                            : 'All'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function WorkflowTemplatesPage() {
  const { divisionId: urlDivisionId } = useParams<{ divisionId?: string }>();
  const { selectedDivisionId, setSelectedDivisionId, divisions } = useDivisionContext();
  const navigate = useNavigate();
  const { isAuthenticated, isSupport, isAdmin } = useAuth();

  const divisionId = urlDivisionId || selectedDivisionId;

  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(true);
  const { data: templates = [], isLoading, error } = useWorkflowTemplates(divisionId ?? '', activeFilter, !!divisionId);
  const deleteMutation = useDeleteWorkflowTemplate(divisionId ?? '');

  const [editorTarget, setEditorTarget] = useState<WorkflowTemplateDto | null | 'new'>(null);
  const [deleteTarget, setDeleteTarget] = useState<WorkflowTemplateDto | null>(null);

  const handleDivisionChange = (id: string) => {
    setSelectedDivisionId(id);
    if (id) navigate(`/workflow-templates/${id}`);
    else navigate('/workflow-templates');
  };

  const handleDelete = async () => {
    if (!deleteTarget || !divisionId) return;
    await deleteMutation.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
  };

  if (!isAuthenticated) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
              Authentication Required
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400">
              Please log in to view or manage workflow templates.
            </p>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Workflow Templates</h1>
            <p className="text-gray-500 dark:text-secondary-400 mt-1">
              Reusable templates that define routing rules, steps, and order attributes for new workflows.
            </p>
          </div>
          {divisionId && !isSupport && (
            <Button onClick={() => setEditorTarget('new')}>
              <Plus className="h-4 w-4 mr-2" /> New Template
            </Button>
          )}
        </div>

        {/* Division selector */}
        <Card className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="max-w-md flex-1">
              <Select
                label="Select Division"
                value={divisionId ?? ''}
                onChange={e => handleDivisionChange(e.target.value)}
              >
                <option value="">Choose a division…</option>
                {divisions.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name} {d.code ? `(${d.code})` : ''}
                  </option>
                ))}
              </Select>
            </div>
            <div className="max-w-xs flex-1">
              <Select
                label="Status"
                value={activeFilter === undefined ? '' : activeFilter.toString()}
                onChange={e => {
                  const value = e.target.value;
                  setActiveFilter(value === '' ? undefined : value === 'true');
                }}
              >
                <option value="true">Active Only</option>
                <option value="false">Inactive Only</option>
                <option value="">All Statuses</option>
              </Select>
            </div>
          </div>
        </Card>

        {/* Template list */}
        {divisionId && (
          <>
            {isLoading && (
              <div className="text-center py-12 text-gray-400 dark:text-secondary-500">Loading templates…</div>
            )}
            {error && (
              <div className="text-center py-12 text-red-500">Failed to load templates.</div>
            )}
            {!isLoading && !error && templates.length === 0 && (
              <Card className="p-12 text-center">
                <Copy className="h-10 w-10 text-gray-300 dark:text-secondary-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-secondary-400 font-medium">No templates yet</p>
                <p className="text-gray-400 dark:text-secondary-500 text-sm mt-1">
                  Create a template to speed up workflow setup.
                </p>
                {!isSupport && (
                  <Button className="mt-4" onClick={() => setEditorTarget('new')}>
                    <Plus className="h-4 w-4 mr-2" /> Create First Template
                  </Button>
                )}
              </Card>
            )}
            <div className="space-y-4">
              {templates.map(t => (
                <TemplateCard
                  key={t.id}
                  template={t}
                  onEdit={() => setEditorTarget(t)}
                  onDelete={() => setDeleteTarget(t)}
                  readOnly={isSupport}
                  canDelete={isAdmin}
                />
              ))}
            </div>
          </>
        )}

        {!divisionId && !isLoading && (
          <Card className="p-12 text-center text-gray-400 dark:text-secondary-500">
            Select a division to manage its workflow templates.
          </Card>
        )}
      </div>

      {/* Editor modal */}
      {editorTarget !== null && divisionId && !isSupport && (
        <TemplateEditor
          divisionId={divisionId}
          template={editorTarget === 'new' ? undefined : editorTarget}
          onClose={() => setEditorTarget(null)}
        />
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <Modal
          isOpen
          onClose={() => setDeleteTarget(null)}
          title="Delete Template"
        >
          <p className="text-gray-700 dark:text-secondary-200">
            Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This cannot be undone.
          </p>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </Modal>
      )}
    </SidebarLayout>
  );
}
